import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, ChevronLeft, ChevronRight } from 'lucide-react';

// Appréciations générales: one remark per grading period plus the
// end-of-year remark and the "Décision de fin d'année". Admin-only
// (backend enforces); printed at the bottom of the bulletin.
// French and Arabic lines can go in the same box — line breaks are kept.
// Arrows move between students; unsaved text asks before discarding.
const StudentRemarksModal = ({ apiCall, year, students, startIndex = 0, onClose, onSaved, showSuccess, showError }) => {
  const [idx, setIdx] = useState(startIndex);
  const [periods, setPeriods] = useState([]);
  const [byPeriod, setByPeriod] = useState({});   // PeriodID -> remark text
  const [finalRemark, setFinalRemark] = useState('');
  const [decision, setDecision] = useState('');
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const student = students[idx];

  const navigate = (dir) => {
    if (dirty && !window.confirm('Unsaved remarks — discard them?')) return;
    setIdx(i => i + dir);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setFinalRemark(''); setDecision(''); setByPeriod({});
      const [pds, remarks] = await Promise.all([
        apiCall(`/academic/grading/periods?academicYear=${year}&className=${encodeURIComponent(student.Class || student.ClassName || '')}`),
        apiCall(`/academic/reports/remarks?studentId=${encodeURIComponent(student.StudentID)}&academicYear=${year}`)
      ]);
      setPeriods(pds);
      const map = {};
      for (const r of remarks) {
        if (r.PeriodID) map[r.PeriodID] = r.Remark || '';
        else { setFinalRemark(r.Remark || ''); setDecision(r.Decision || ''); }
      }
      setByPeriod(map);
      setDirty(false);
    } catch (err) { showError(err.message); }
    finally { setLoading(false); }
  }, [apiCall, year, student.StudentID, student.Class, student.ClassName, showError]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const entries = periods.map(p => ({ periodId: p.PeriodID, remark: byPeriod[p.PeriodID] || '' }));
      entries.push({ periodId: null, remark: finalRemark, decision });
      const r = await apiCall('/academic/reports/remarks', {
        method: 'PUT',
        body: JSON.stringify({ studentId: student.StudentID, academicYear: year, entries })
      });
      showSuccess(r.message);
      setDirty(false);
      onSaved?.();
    } catch (err) { showError(err.message); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl max-h-[85vh] overflow-auto">
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} disabled={idx === 0}
              className="p-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-30" title="Previous student">
              <ChevronLeft size={16} />
            </button>
            <h3 className="text-lg font-semibold">
              Appréciations — {student.FirstName} {student.LastName}
              <span className="ml-2 text-xs text-gray-400 font-normal">{idx + 1} / {students.length}</span>
            </h3>
            <button onClick={() => navigate(1)} disabled={idx >= students.length - 1}
              className="p-1.5 border rounded-lg hover:bg-gray-50 disabled:opacity-30" title="Next student">
              <ChevronRight size={16} />
            </button>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">{student.Class} {student.Section || ''} · {year} — printed at the bottom of the bulletin.</p>

        {loading ? (
          <p className="text-center text-gray-500 py-6">Loading…</p>
        ) : (
          <div className="space-y-3">
            {periods.map(p => (
              <div key={p.PeriodID}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{p.PeriodName}</label>
                <textarea
                  value={byPeriod[p.PeriodID] || ''}
                  onChange={e => { setByPeriod(m => ({ ...m, [p.PeriodID]: e.target.value })); setDirty(true); }}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Appréciation (français / عربي)…"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarque de fin d'année</label>
              <textarea
                value={finalRemark}
                onChange={e => { setFinalRemark(e.target.value); setDirty(true); }}
                rows={3}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="Remarque générale de fin d'année…"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Décision de fin d'année</label>
              <input
                type="text"
                value={decision}
                onChange={e => { setDecision(e.target.value); setDirty(true); }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="ex : Admis(e) en CE1."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={save} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1">
                <Save size={14} /> Save Remarks
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentRemarksModal;
